<script setup lang="ts">
// "Github punch-card" — 2D scatter where dot size encodes count.
// Generic 2-categorical-axis chart; canonical day-of-week × hour-of-day
// case is the most common but the spec accepts any pair.
//
// `xLabel` / `yLabel` are required when neither axis matches the
// temporal allowlist (validator-enforced); in the canonical case they
// are optional and the axis names alone suffice.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { PunchcardChartSpec } from "../sbi/protocol";
import { COLORS } from "../theme.ts";
import {
  axisLabelDefaults,
  axisLineDefaults,
  chartChrome,
  tooltipDefaults,
} from "./echarts-base";
import {
  categoryLabelPolicy,
  categoryLabelWidth,
  dataZoomOption,
  NARROW,
  plotWidthEstimate,
} from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: PunchcardChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const xCategories = computed(() => {
  const set = new Set<string | number>();
  for (const r of rows.value) {
    const v = r[props.spec.x];
    if (typeof v === "string" || typeof v === "number") set.add(v);
  }
  return [...set];
});

const yCategories = computed(() => {
  const set = new Set<string | number>();
  for (const r of rows.value) {
    const v = r[props.spec.y];
    if (typeof v === "string" || typeof v === "number") set.add(v);
  }
  return [...set];
});

const maxSize = computed(() => {
  let m = 0;
  for (const r of rows.value) {
    const v = Number(r[props.spec.size]);
    if (Number.isFinite(v) && v > m) m = v;
  }
  return m || 1;
});

const option = computed(() => {
  const points = rows.value.map((r) => {
    const xi = xCategories.value.indexOf(r[props.spec.x] as string | number);
    const yi = yCategories.value.indexOf(r[props.spec.y] as string | number);
    const size = Number(r[props.spec.size]);
    return [xi, yi, Number.isFinite(size) ? size : 0];
  });
  const w = ctx.value.width;
  const narrow = w > 0 && w < NARROW;
  // Row labels: capped like horizontal-bar labels. The rotated y name
  // sits just outside them (it used to sit at a fixed 50px, inside
  // long labels); on narrow charts it moves above the axis instead.
  const yLabelWidth = categoryLabelWidth(yCategories.value, w);
  const hasYName = !!props.spec.yLabel;
  const hasXName = !!props.spec.xLabel;
  const zoom = dataZoomOption(props.spec.dataZoom, ctx.value);
  const top = narrow && hasYName ? 48 : 32;
  const bottom = (hasXName ? 28 : 8) + zoom.reserveBottom;
  // Bubbles scale to their cell: a fixed 40px cap overlapped into a
  // blob once 24 hour-columns shared a phone-width plot.
  const plotW = Math.max(0, plotWidthEstimate(w) - yLabelWidth);
  const plotH = Math.max(0, height.value - top - bottom - 20);
  const cell = Math.min(
    plotW / Math.max(1, xCategories.value.length),
    plotH / Math.max(1, yCategories.value.length),
  );
  const maxSymbol = w > 0 ? Math.min(40, Math.max(6, cell * 0.9)) : 36;
  return {
    ...chartChrome(ctx.value),
    grid: {
      containLabel: true,
      left: hasYName && !narrow ? 26 : 8,
      right: 16,
      top,
      bottom,
    },
    tooltip: tooltipDefaults("item", ctx.value),
    dataZoom: zoom.dataZoom,
    xAxis: {
      type: "category" as const,
      data: xCategories.value,
      name: props.spec.xLabel,
      nameLocation: "middle" as const,
      nameGap: 30,
      // Mono axis-name label (`xLabel`) — matches the tick treatment
      // below; without this the name reads in a different family from
      // its own tick row.
      nameTextStyle: { ...axisLabelDefaults() },
      ...axisLineDefaults(),
      axisTick: { show: false },
      axisLabel: {
        ...axisLabelDefaults(),
        ...categoryLabelPolicy({
          labels: xCategories.value,
          plotWidth: plotW,
          mode: "thin",
        }),
      },
    },
    yAxis: {
      type: "category" as const,
      data: yCategories.value,
      name: props.spec.yLabel,
      ...(narrow
        ? {
            nameLocation: "end" as const,
            nameGap: 8,
            nameTextStyle: { ...axisLabelDefaults(), align: "left" as const },
          }
        : {
            nameLocation: "middle" as const,
            nameGap: yLabelWidth + 14,
            nameTextStyle: { ...axisLabelDefaults() },
          }),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisLabelDefaults(),
        ...(w > 0 ? { width: yLabelWidth, overflow: "truncate" as const } : {}),
      },
    },
    series: [
      {
        type: "scatter" as const,
        symbolSize: (val: [number, number, number]) =>
          Math.max(2, (val[2] / maxSize.value) * maxSymbol),
        data: points,
        itemStyle: { color: COLORS.primary, opacity: 0.7 },
      },
    ],
  };
});

const height = computed(() =>
  resolveChartHeight("punchcard", props.spec.height),
);

function onClick(p: { dataIndex?: number; data?: unknown }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const point = p.data as [number, number, number] | undefined;
  if (!point) return;
  emit("click-target", props.spec.id, {
    x: xCategories.value[point[0]],
    y: yCategories.value[point[1]],
    size: point[2],
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
